import { MC_COLORS, isHexColor, isNamedColor, nearestNamedColor, normalizeHex } from './colors';
import { applyGradient, graphemes } from './gradient';
import {
  CLICK_ACTIONS,
  type ClickAction,
  type Compat,
  type CompileNote,
  type CompileNoteKind,
  type CompileResult,
  type HoverRun,
  type JsonComponent,
  type NamedColor,
  type RawRun,
  type RunStyle,
  type TellrawMessage,
  type TextRun,
} from './types';
import { effectiveCompat } from './versions';

/**
 * Compiling a composed message into ONE `tellraw` console command.
 *
 * A schedule sends the same command to every server it targets, and those
 * servers span three dialects (see versions.ts). Keeping a command per
 * dialect would mean three things to review and three to drift apart, so the
 * compiler writes one command that every family switched on reads correctly:
 *
 * - Hex colours need 1.16. With 1.15 and older on, hex steps down to the
 *   nearest named colour for everyone, and a gradient steps through named
 *   colours, each change reported as a note so staff are not surprised.
 * - 1.21.5 renamed `clickEvent`/`hoverEvent` to `click_event`/`hover_event`
 *   (with new field names), and every version ignores keys it does not know,
 *   so with both sides on each event is written in both spellings.
 * - 1.21.5 reads text components as SNBT. JSON with quoted keys is valid SNBT,
 *   so the output stays JSON, and every list holds objects only, since older
 *   parsers and SNBT's homogeneous lists both prefer it.
 *
 * The root is an unstyled `{"text":""}` whose `extra` holds every piece as a
 * sibling, so nothing inherits from anything else: each piece states exactly
 * the style it has, and style flags are written only when true. Key order is
 * fixed so the same message always compiles to the same bytes.
 */

/** Largest command one RCON packet carries; longer ones fail on servers reached through RCON. */
export const RCON_PACKET_LIMIT = 1446;

/** Where a 1.21.5+ `click_event` keeps its value, by action. */
export const CLICK_EVENT_FIELDS: Record<ClickAction, 'url' | 'command' | 'value'> = {
  open_url: 'url',
  run_command: 'command',
  suggest_command: 'command',
  copy_to_clipboard: 'value',
};

const STYLE_FLAGS = ['bold', 'italic', 'underlined', 'strikethrough', 'obfuscated'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function isClickAction(value: unknown): value is ClickAction {
  return (CLICK_ACTIONS as readonly unknown[]).includes(value);
}

export function isRawRun(run: unknown): run is RawRun {
  return isRecord(run) && isRecord(run.raw);
}

/** A deep copy of JSON-shaped data. `Object.fromEntries` keeps a `__proto__` key as plain data. */
function copyJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(copyJson);
  if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, copyJson(inner)]));
  return value;
}

/** Up to ~24 characters of the affected text, on one line, for a note. Blank text reads as “ ”. */
function quote(text: string): string {
  const parts = graphemes(text.replace(/\s+/g, ' ').trim());
  const shown = parts.length > 24 ? `${parts.slice(0, 23).join('').trimEnd()}…` : parts.join('');
  return `“${shown || ' '}”`;
}

const colourName = (color: NamedColor) => color.replace(/_/g, ' ');

function listOf(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function describeRaw(raw: Record<string, unknown>): string {
  const str = (value: unknown) => (typeof value === 'string' ? value : '');
  if (typeof raw.text === 'string' && raw.text.trim()) return `text ${quote(raw.text)}`;
  if (str(raw.translate)) return `translation ${quote(str(raw.translate))}`;
  if (str(raw.selector)) return `selector ${quote(str(raw.selector))}`;
  if (isRecord(raw.score) && str(raw.score.objective)) return `score ${quote(str(raw.score.objective))}`;
  if (str(raw.keybind)) return `keybind ${quote(str(raw.keybind))}`;
  if (str(raw.nbt)) return `NBT value ${quote(str(raw.nbt))}`;
  return 'part';
}

class Notes {
  readonly list: CompileNote[] = [];
  private readonly seen = new Set<string>();

  add(kind: CompileNoteKind, message: string): void {
    const key = `${kind}\u0000${message}`;
    if (this.seen.has(key)) return;
    this.seen.add(key);
    this.list.push({ kind, message });
  }
}

interface Piece {
  text: string;
  color: string | null;
}

function compileColour(color: unknown, text: string, legacy: boolean, notes: Notes): string | null {
  if (isNamedColor(color)) return color;
  if (typeof color !== 'string' || !isHexColor(color)) return null;
  const hex = normalizeHex(color);
  if (!legacy) return hex;
  const named = nearestNamedColor(hex);
  if (MC_COLORS[named] !== hex) {
    notes.add(
      'hex_to_named',
      `The colour ${hex} on ${quote(text)} becomes ${colourName(named)}, the nearest one 1.15 and older can show.`,
    );
  }
  return named;
}

/** The run's text cut into pieces of one colour, with the colour rules for the families on applied. */
function colourPieces(text: string, style: RunStyle, legacy: boolean, notes: Notes): Piece[] {
  const stops = Array.isArray(style.gradient)
    ? style.gradient.filter(stop => isNamedColor(stop) || isHexColor(stop))
    : [];
  if (stops.length >= 2) {
    const pieces = applyGradient(text, stops);
    if (!legacy) return pieces;
    const stepped: { text: string; color: NamedColor }[] = [];
    for (const piece of pieces) {
      const color = nearestNamedColor(piece.color);
      const last = stepped[stepped.length - 1];
      if (last && last.color === color) last.text += piece.text;
      else stepped.push({ text: piece.text, color });
    }
    const used = [...new Set(stepped.map(piece => colourName(piece.color)))];
    notes.add(
      'gradient_stepped',
      used.length === 1
        ? `The gradient on ${quote(text)} becomes solid ${used[0]}.`
        : `The gradient on ${quote(text)} steps through ${listOf(used)}.`,
    );
    return stepped;
  }
  return [{ text, color: compileColour(stops.length === 1 ? stops[0] : style.color, text, legacy, notes) }];
}

function styledNode(piece: Piece, style: RunStyle): JsonComponent {
  const node: JsonComponent = { text: piece.text };
  if (piece.color) node.color = piece.color;
  for (const flag of STYLE_FLAGS) {
    if (style[flag] === true) node[flag] = true;
  }
  return node;
}

function compileHover(runs: unknown, legacy: boolean, notes: Notes): JsonComponent | null {
  const extra: JsonComponent[] = [];
  for (const run of Array.isArray(runs) ? (runs as HoverRun[]) : []) {
    if (!isRecord(run) || typeof run.text !== 'string' || run.text === '') continue;
    for (const piece of colourPieces(run.text, run, legacy, notes)) extra.push(styledNode(piece, run));
  }
  return extra.length > 0 ? { text: '', extra } : null;
}

/** The run's click and hover in every spelling the families on need, in the fixed key order. */
function compileEvents(run: TextRun, on: Compat, notes: Notes): JsonComponent {
  const events: JsonComponent = {};
  const older = on.legacy || on.modern;
  const click = run.click;
  if (isRecord(click) && isClickAction(click.action) && typeof click.value === 'string') {
    if (older) events.clickEvent = { action: click.action, value: click.value };
    if (on.current) events.click_event = { action: click.action, [CLICK_EVENT_FIELDS[click.action]]: click.value };
    if (on.legacy && click.action === 'copy_to_clipboard') {
      notes.add('copy_legacy', `The copy-to-clipboard click on ${quote(run.text)} does nothing on 1.14 and older.`);
    }
  }
  const hover = isRecord(run.hover) ? compileHover(run.hover.runs, on.legacy, notes) : null;
  if (hover) {
    if (older) {
      const hoverEvent: JsonComponent = { action: 'show_text' };
      if (on.modern) hoverEvent.contents = hover;
      if (on.legacy) hoverEvent.value = copyJson(hover);
      events.hoverEvent = hoverEvent;
    }
    if (on.current) events.hover_event = { action: 'show_text', value: copyJson(hover) };
  }
  if (older && on.current && (events.clickEvent || events.hoverEvent)) {
    notes.add(
      'dual_events',
      'Click and hover events are written twice, once for 1.21.4 and older and once for 1.21.5 and newer.',
    );
  }
  return events;
}

/**
 * The message as one JSON text component, plus notes on what was changed or
 * cannot work for the families on. With no family on, all three are.
 */
export function compileComponent(message: TellrawMessage, compat: Compat): { component: JsonComponent; notes: CompileNote[] } {
  const on = effectiveCompat(compat);
  const notes = new Notes();
  const extra: JsonComponent[] = [];
  const runs: unknown[] = Array.isArray(message?.runs) ? message.runs : [];
  for (const run of runs) {
    if (isRawRun(run)) {
      extra.push(copyJson(run.raw) as JsonComponent);
      notes.add('raw_kept', `The imported ${describeRaw(run.raw)} is kept exactly as it was written.`);
      continue;
    }
    if (!isRecord(run) || typeof run.text !== 'string' || run.text === '') continue;
    const textRun = run as unknown as TextRun;
    const pieces = colourPieces(textRun.text, textRun, on.legacy, notes);
    const events = compileEvents(textRun, on, notes);
    for (const piece of pieces) extra.push({ ...styledNode(piece, textRun), ...(copyJson(events) as JsonComponent) });
  }
  const component: JsonComponent = { text: '' };
  if (extra.length > 0) component.extra = extra;
  return { component, notes: notes.list };
}

function utf8Length(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** The whole console command for `selector`, with its size checked against one RCON packet. */
export function compileTellraw(message: TellrawMessage, compat: Compat, selector = '@a'): CompileResult {
  const { component, notes } = compileComponent(message, compat);
  const json = JSON.stringify(component);
  const command = `tellraw ${selector} ${json}`;
  const bytes = utf8Length(command);
  if (bytes > RCON_PACKET_LIMIT) {
    notes.push({
      kind: 'rcon_length',
      message: `The command is ${bytes} bytes, more than the ${RCON_PACKET_LIMIT} that fit in one RCON packet, so servers reached through RCON cannot run it.`,
    });
  }
  return { command, json, component, notes, bytes };
}
